import './Header.scss';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Switch from 'react-switch';
import {
  HEADER_MENU_EVENT,
  isMobileViewport
} from '../../../utils/mobileUtil';
import {
  getLanguageDisplayName,
  dedupeLanguageCodes
} from '../../../utils/languageUtil';

const formatMs = value => {
  if (typeof value !== 'number') return 'not available';
  return `${Math.round(value)}ms`;
};

const getSpeedLabel = connection => {
  if (!connection) return 'moderate';

  const downlink = connection.downlinkMbps;
  const rtt = connection.rttMs;
  const effectiveType = connection.effectiveType;

  const isSlow =
    effectiveType === 'slow-2g' ||
    effectiveType === '2g' ||
    (typeof downlink === 'number' && downlink > 0 && downlink < 0.8) ||
    (typeof rtt === 'number' && rtt > 1200);

  if (isSlow) return 'slow';

  const isModerate =
    effectiveType === '3g' ||
    (typeof downlink === 'number' && downlink > 0 && downlink < 1.5) ||
    (typeof rtt === 'number' && rtt > 600);

  if (isModerate) return 'moderate';
  return 'fast';
};

const buildNetworkTooltip = (quality, stats) => {
  const connection = stats && stats.connection ? stats.connection : null;
  const connBits = [];
  if (connection?.effectiveType)
    connBits.push(`type ${connection.effectiveType}`);
  if (typeof connection?.downlinkMbps === 'number')
    connBits.push(`down ${connection.downlinkMbps}Mbps`);
  if (typeof connection?.rttMs === 'number')
    connBits.push(`rtt ${connection.rttMs}ms`);

  const connText = connBits.length > 0 ? ` (${connBits.join(', ')})` : '';
  const speedLabel =
    quality === 'offline'
      ? 'offline'
      : quality === 'healthy'
      ? 'fast'
      : quality === 'degraded'
      ? 'moderate'
      : quality === 'poor'
      ? getSpeedLabel(connection)
      : getSpeedLabel(connection);
  const downlinkText =
    typeof connection?.downlinkMbps === 'number'
      ? ` (${connection.downlinkMbps}Mbps)`
      : '';
  return (
    `Network: ${speedLabel}${connText}\n` +
    `Speed: ${speedLabel}${downlinkText}\n` +
    `Network response time: ${formatMs(connection?.rttMs)}`
  );
};

class Header extends Component {
  constructor(props) {
    super(props);
    this.state = {
      timeSince: '00:00',
      language: 'en',
      timeInSeconds: 300,
      menuOpen: false,
      isMobile: isMobileViewport(),
      navOpen: false,
      showClustersInfo: false,
      showNetworkQualityInfo: false
    };
    this.timer = 0;
    this.networkQualityInfoTimer = null;
    this.startTimer = this.startTimer.bind(this);
    this.countDown = this.countDown.bind(this);
    this.handleLogoutSubmission = this.handleLogoutSubmission.bind(this);
    this.toggleMenu = this.toggleMenu.bind(this);
    this.toggleNav = this.toggleNav.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleGlobalKeyDown);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener(HEADER_MENU_EVENT, this.handleHeaderMenuEvent);
    this.startTimer();
    let storageLanguage = localStorage.getItem('language');
    let storageTime = localStorage.getItem('updateInterval');
    let storageShowAgentNames = localStorage.getItem('showAgentNames');
    let storageHideOldAgents = localStorage.getItem('hideOldAgents');
    let storageShowInactiveAgents = localStorage.getItem('showInactiveAgents');
    let storageShowClusters = localStorage.getItem('showClusters');
    let storageTrackColor = localStorage.getItem('trackColor');
    if (storageTime) {
      this.setState({ timeInSeconds: storageTime });
      this.props.setUpdateInterval(storageTime);
    }
    if (storageLanguage) {
      this.setState({ language: storageLanguage });
      this.props.setSelectedLanguage(storageLanguage);
    } else {
      this.props.setSelectedLanguage('en');
    }
    if (storageShowAgentNames !== null) {
      const value = storageShowAgentNames === 'true';
      this.props.setShowAgentNames(value);
    }
    if (storageHideOldAgents !== null) {
      const value = storageHideOldAgents === 'true';
      this.props.setHideOldAgents(value);
    }
    if (storageShowInactiveAgents !== null) {
      const value = storageShowInactiveAgents === 'true';
      this.props.setShowInactiveAgents(value);
    }
    if (storageShowClusters !== null) {
      const value = storageShowClusters === 'true';
      this.props.setShowObservationClusters(value);
    }
    if (storageTrackColor !== null) {
      this.props.setTrackColor(storageTrackColor);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener(HEADER_MENU_EVENT, this.handleHeaderMenuEvent);
    document.removeEventListener('keydown', this.handleGlobalKeyDown);
    if (this.networkQualityInfoTimer) {
      clearTimeout(this.networkQualityInfoTimer);
      this.networkQualityInfoTimer = null;
    }
  }

  showNetworkQualityInfo = e => {
    if (e) e.stopPropagation();
    this.setState({ showNetworkQualityInfo: true });
    if (this.networkQualityInfoTimer)
      clearTimeout(this.networkQualityInfoTimer);
    this.networkQualityInfoTimer = setTimeout(() => {
      this.setState({ showNetworkQualityInfo: false });
    }, 8000);
  };

  hideNetworkQualityInfo = () => {
    if (this.state.showNetworkQualityInfo) {
      this.setState({ showNetworkQualityInfo: false });
    }
  };

  startTimer() {
    if (this.timer === 0) {
      this.timer = setInterval(this.countDown, 500);
    }
  }

  countDown = async () => {
    const timeSince = this.props.LKPUpdate
      ? moment.utc(moment().diff(moment(this.props.LKPUpdate))).format('mm:ss')
      : '00:00';
    if (timeSince !== this.state.timeSince) {
      this.setState({ timeSince });
    }
  };

  handleLogoutSubmission = e => {
    e.preventDefault();
    this.props.submitLogout();
  };

  onLanguageChange = e => {
    this.setState({ language: e.target.value });
    this.props.setSelectedLanguage(e.target.value);
    localStorage.setItem('language', e.target.value);
  };

  onIntervalChange = e => {
    this.setState({ timeInSeconds: e.target.value });
    this.props.setUpdateInterval(e.target.value);
    localStorage.setItem('updateInterval', e.target.value);
  };

  onShowAgentNamesChange = value => {
    this.props.setShowAgentNames(value);
    localStorage.setItem('showAgentNames', value);
  };

  onHideOldAgentsChange = value => {
    const nextValue = !value;
    this.props.setHideOldAgents(nextValue);
    localStorage.setItem('hideOldAgents', nextValue);
  };

  onShowInactiveAgentsChange = value => {
    this.props.setShowInactiveAgents(value);
    localStorage.setItem('showInactiveAgents', value);
  };

  onShowClustersChange = value => {
    this.props.setShowObservationClusters(value);
    localStorage.setItem('showClusters', value);
  };

  onClusterThresholdChange = e => {
    const value = Number(e.target.value);
    this.props.setObservationClusterThreshold(value);
    localStorage.setItem('clusterThreshold', value);
  };

  // Single preference driving the default (centerpoints-mode) track line's
  // style - despite the name, 'trackColor' now covers all 3 mutually
  // exclusive options (yellow/blue/colour coded by hour of day), not just a
  // literal colour, to avoid two separate toggles ever disagreeing with
  // each other (e.g. "blue" and "colour coded" both on at once).
  onTrackColorChange = e => {
    const color = e.target.value;
    this.props.setTrackColor(color);
    localStorage.setItem('trackColor', color);
  };

  toggleShowClustersInfo = e => {
    e.preventDefault();
    e.stopPropagation();
    this.setState(prevState => ({
      showClustersInfo: !prevState.showClustersInfo
    }));
  };

  onShowGridChange = value => {
    this.props.setShowGrid(value);
  };

  onShowCoordinatesChange = value => {
    this.props.setShowCoordinates(value);
  };

  handleSwitch = value => {
    if (value) {
      this.props.allowAlertSounds();
    } else {
      this.props.disableAlertSounds();
    }
  };

  toggleMenu = e => {
    e.preventDefault();
    this.setState(state => {
      const nextMenuOpen = !state.menuOpen;
      return {
        menuOpen: nextMenuOpen,
        showClustersInfo: nextMenuOpen ? state.showClustersInfo : false
      };
    });
  };

  toggleNav = () => {
    this.setState(state => ({ navOpen: !state.navOpen }));
  };

  handleResize = () => {
    const isMobile = isMobileViewport();
    if (isMobile !== this.state.isMobile) {
      this.setState({ isMobile, menuOpen: false, showClustersInfo: false });
    }
  };

  handleHeaderMenuEvent = e => {
    const detail = (e && e.detail) || {};
    this.setState(state => {
      const menuOpen =
        typeof detail.open === 'boolean' ? detail.open : !state.menuOpen;
      return {
        menuOpen,
        showClustersInfo: menuOpen ? state.showClustersInfo : false
      };
    });
  };

  closeMenu = () => {
    this.setState({
      menuOpen: false,
      showClustersInfo: false
    });
  };

  handleGlobalKeyDown = e => {
    if (e.key === 'Escape' && this.state.menuOpen) {
      this.closeMenu();
    }
  };

  // The preferences menu is defined once. Desktop mounts it in the top bar
  // dropdown; mobile mounts it as a bottom sheet opened from the footer bar.
  renderMenu = () => {
    const props = this.props;
    return (
      <div
        className={`dropdown-menu dropdown-menu-right header-menu${
          this.state.menuOpen ? ' show' : ''
        }`}
        aria-labelledby="dropdownMenuLink"
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
      >
        {this.state.isMobile && (
          <div className="header-menu-status text-muted small mx-4 mb-1">
            Last update: {this.state.timeSince} min ago
          </div>
        )}
        <h6 className="dropdown-header preferences-header">
          Preferences
        </h6>
        <div className="dropdown-divider"></div>
        {/* <div className="form-check pl-5 pr-3 dropdown-item">
          <input className="form-check-input" type="radio" name="gridRadios" id="gridRadios1" value="option1" checked />
          <label className="form-check-label" htmlFor="gridRadios1">
            Dutch
          </label>
        </div>
        <div className="form-check pl-5 pr-3  dropdown-item">
          <input className="form-check-input" type="radio" name="gridRadios" id="gridRadios2" value="option2" />
          <label className="form-check-label" htmlFor="gridRadios2">
            Hindi
          </label>
        </div>
        <div className="form-check pl-5 pr-3 dropdown-item">
          <input className="form-check-input" type="radio" name="gridRadios" id="gridRadios3" value="option3" />
          <label className="form-check-label" htmlFor="gridRadios3">
            English
          </label>
        </div> */}
        <div className="row no-gutters pl-4 align-items-center preferences-toggle">
          <Switch
            height={21}
            width={42}
            onClick={e => console.log(e)}
            onChange={this.handleSwitch}
            checked={this.props.alertSounds}
            uncheckedIcon={false}
            checkedIcon={false}
          />
          <span className="ml-2">Use alert sound</span>
        </div>
        <div className="row no-gutters pl-4 align-items-center preferences-toggle">
          <Switch
            height={21}
            width={42}
            onChange={this.onShowAgentNamesChange}
            checked={this.props.showAgentNames}
            uncheckedIcon={false}
            checkedIcon={false}
          />
          <span className="ml-2">Show agent name on map</span>
        </div>
        <div className="row no-gutters pl-4 align-items-center preferences-toggle">
          <Switch
            height={21}
            width={42}
            onChange={this.onHideOldAgentsChange}
            checked={!this.props.hideOldAgents}
            uncheckedIcon={false}
            checkedIcon={false}
          />
          <span className="ml-2">Show agents older then 7 days</span>
        </div>
        <div className="row no-gutters pl-4 align-items-center preferences-toggle">
          <Switch
            height={21}
            width={42}
            onChange={this.onShowInactiveAgentsChange}
            checked={this.props.showInactiveAgents}
            uncheckedIcon={false}
            checkedIcon={false}
          />
          <span className="ml-2">
            Show inactive agents in Agent list
          </span>
        </div>
        <div className="row no-gutters pl-4 align-items-center preferences-toggle">
          <Switch
            height={21}
            width={42}
            onChange={this.onShowClustersChange}
            checked={this.props.showObservationClusters}
            uncheckedIcon={false}
            checkedIcon={false}
          />
          <span className="ml-2">Use clusters if &gt;</span>
          <select
            className="header-select ml-1 mr-1"
            value={this.props.observationClusterThreshold}
            onChange={this.onClusterThresholdChange}
            disabled={!this.props.showObservationClusters}
          >
            <option value={100}>100</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>
          <span>observations</span>
        </div>
        <div className="row no-gutters pl-4 align-items-center preferences-toggle">
          <Switch
            height={21}
            width={42}
            onChange={this.onShowCoordinatesChange}
            checked={this.props.showCoordinates}
            uncheckedIcon={false}
            checkedIcon={false}
          />
          <span className="ml-2">Show coordinates info panel</span>
        </div>
        <div className="row no-gutters pl-4 align-items-center preferences-toggle">
          <Switch
            height={21}
            width={42}
            onChange={this.onShowGridChange}
            checked={this.props.showGrid}
            uncheckedIcon={false}
            checkedIcon={false}
          />
          <span className="ml-2">Show WGS 84 grid</span>
        </div>
        <div className="row no-gutters pl-4 align-items-center preferences-toggle">
          <span className="header-select-label">Track line colour</span>
          <select
            className="header-select mr-2"
            value={this.props.trackColor}
            onChange={this.onTrackColorChange}
          >
            <option value="yellow">Yellow</option>
            <option value="blue">Blue</option>
            <option value="colourCoded">
              Colour coded by hour of day (single track only)
            </option>
          </select>
        </div>
        <div
          className="row no-gutters pl-4 align-items-center preferences-toggle"
          onClick={e => e.stopPropagation()}
        >
          <span className="header-select-label">
            Update time interval
          </span>
          <select
            className="header-select mr-2"
            value={this.state.timeInSeconds}
            onChange={this.onIntervalChange}
            size="1"
            onMouseDown={e => e.stopPropagation()}
          >
            {[
              { title: '1 Min', time: 60 },
              { title: '5 Min', time: 300 },
              { title: '10 Min', time: 600 }
            ].map((item, key) => (
              <option key={key} value={item.time}>
                {item.title}
              </option>
            ))}
          </select>
        </div>
        <div
          className="row no-gutters pl-4 align-items-center preferences-toggle"
          onClick={e => e.stopPropagation()}
        >
          <span className="header-select-label">Language</span>
          <select
            className="header-select mr-2"
            value={this.state.language}
            onChange={this.onLanguageChange}
            size="1"
            onMouseDown={e => e.stopPropagation()}
          >
            {dedupeLanguageCodes(props.languages).map((item, key) => (
              <option key={key} value={item}>
                {getLanguageDisplayName(item)}
              </option>
            ))}
          </select>
        </div>
        <div role="separator" className="dropdown-divider"></div>
        <a
          className="dropdown-item"
          href="https://sensingclues.freshdesk.com/support/home"
          target="_blank"
          rel="noopener noreferrer"
        >
          Helpdesk
        </a>
        <a
          className="dropdown-item"
          href="https://sensingclues.freshdesk.com/support/solutions/48000458580"
          target="_blank"
          rel="noopener noreferrer"
        >
          Release notes
        </a>
        <div role="separator" className="dropdown-divider"></div>
        <small className="text-muted mx-4">
          {props.currentUsername}
        </small>
        <div
          className="dropdown-item logout-item"
          style={{ cursor: 'pointer' }}
          onClick={this.handleLogoutSubmission}
        >
          <span>Logout</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 24 24"
            className="logout-icon"
          >
            <path d="M17 7L15.59 8.41 18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5-5-5z" />
            <path d="M4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
          </svg>
        </div>
      </div>
    );
  };

  render() {
    const { ...props } = this.props;
    return (
      <nav className="navbar navbar-expand-md navbar-dark bg-primary py-1 app-topbar">
        <a
          className="navbar-brand"
          href="https://sensingclues.org"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/assets/img/logo/logo_text.svg"
            className="navbar-brand-logo"
            width="auto"
            height="49"
            alt="Sensing Clues"
          />
        </a>
        {props.isAuthenticated && (
          <span className="app-title-center">Focus 360</span>
        )}

        <button
          className="navbar-toggler"
          type="button"
          aria-controls="navbarSupportedContent"
          aria-expanded={this.state.navOpen}
          aria-label="Toggle navigation"
          onClick={this.toggleNav}
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        {props.isAuthenticated && (
          <div
            className={`collapse navbar-collapse${
              this.state.navOpen ? ' show' : ''
            }`}
            id="navbarSupportedContent"
          >
            <ul className="navbar-nav mr-auto"></ul>

            {/* <ul className="navbar-nav flex-row ml-md-auto d-md-flex">
              <li className="nav-item dropdown mr-3">
                <span className="nav-item nav-link">
                 BE: { props.pingInfo.backend ? props.pingInfo.backend.version : "n/a" }
                </span>
              </li>
              <li className="nav-item dropdown mr-3">
                <span className="nav-item nav-link">
                 MT: { props.pingInfo.version ? props.pingInfo.version : "n/a"  }
                </span>
              </li>
              <li className="nav-item dropdown mr-3">
                <span className="nav-item nav-link">
                 FE: { process.env.REACT_APP_VERSION }
                </span>
              </li>
            </ul> */}

            <div className="col-auto pr-2">
              {props.alertsTotal > 0 && (
                <span
                  type="button"
                  className="btn btn-danger float-right font-weight-bold pulse-button"
                  data-toggle="collapse"
                  href="#multiCollapseExample2"
                  role="button"
                  aria-expanded="false"
                  aria-controls="multiCollapseExample1"
                  onClick={() => this.props.setAlertTimeline(true)}
                >
                  {props.alertsTotal} Alert{props.alertsTotal > 1 ? 's' : ''}
                </span>
              )}
              <div className="float-right header-update-status">
                <span className="network-quality-wrap">
                  <button
                    type="button"
                    className={`network-quality-lamp network-quality-${props.networkQuality ||
                      'unknown'}`}
                    title={buildNetworkTooltip(
                      props.networkQuality,
                      props.networkStats
                    )}
                    aria-label={buildNetworkTooltip(
                      props.networkQuality,
                      props.networkStats
                    )}
                    onClick={this.showNetworkQualityInfo}
                    onBlur={this.hideNetworkQualityInfo}
                  />
                  {this.state.showNetworkQualityInfo && (
                    <div
                      className="network-quality-popover"
                      role="tooltip"
                      onMouseDown={e => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      {buildNetworkTooltip(
                        props.networkQuality,
                        props.networkStats
                      )
                        .split('\n')
                        .map((line, index) => (
                          <div key={index} style={{ whiteSpace: 'nowrap' }}>
                            {line}
                          </div>
                        ))}
                      <div
                        style={{
                          whiteSpace: 'nowrap',
                          marginTop: '0.35rem',
                          borderTop: '1px solid rgba(148,163,184,0.4)',
                          paddingTop: '0.35rem'
                        }}
                      >
                        <a
                          href="https://sensingclues.freshdesk.com/support/solutions/articles/48001282401-how-can-i-improve-my-internet-speed-"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#0066cc' }}
                        >
                          More information
                        </a>
                      </div>
                    </div>
                  )}
                </span>
                <span className="text-light p-2 mr-1 font-weight-bold tabular-numbs header-update-text">
                  Last update: {this.state.timeSince} min ago
                </span>
              </div>
            </div>
            <div className="dropdown">
              <a
                className="btn btn-light dropdown-toggle"
                href="/"
                role="button"
                id="dropdownMenuLink"
                aria-haspopup="true"
                aria-expanded={this.state.menuOpen}
                onClick={this.toggleMenu}
              >
                <svg
                  className="bi bi-people-circle"
                  width="1em"
                  height="1em"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M13.468 12.37C12.758 11.226 11.195 10 8 10s-4.757 1.225-5.468 2.37A6.987 6.987 0 008 15a6.987 6.987 0 005.468-2.63z" />
                  <path
                    fillRule="evenodd"
                    d="M8 9a3 3 0 100-6 3 3 0 000 6z"
                    clipRule="evenodd"
                  />
                  <path
                    fillRule="evenodd"
                    d="M8 1a7 7 0 100 14A7 7 0 008 1zM0 8a8 8 0 1116 0A8 8 0 010 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              {!this.state.isMobile && this.renderMenu()}
            </div>
          </div>
        )}
        {props.isAuthenticated && this.state.isMobile && this.renderMenu()}
        {this.state.menuOpen && (
          <div
            className="header-menu-scrim"
            onClick={this.closeMenu}
            role="button"
            tabIndex={-1}
            aria-label="Close menu"
          />
        )}
      </nav>
    );
  }
}

Header.propTypes = {
  submitLogout: PropTypes.func,
  networkQuality: PropTypes.string,
  networkStats: PropTypes.object
};

export default Header;
